/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import { Link } from 'react-router-dom';
import {
  isBraindateCancelled,
  isBraindateDeclined,
  getBraindateConversation,
  getBraindatePath,
  getBraindateId,
} from '@e180/domain/lib/braindate/util';
import {
  getConversationTopic,
  getConversationUnreadNotificationsCount,
} from '@e180/domain/lib/conversation/util';
import { getTopicTitle, isGroupTopic } from '@e180/domain/lib/topic/util';
import { getUserPosition, getUserCompany } from '@e180/domain/lib/user/util';
import { isNumber } from '@e180/util/lib/type';

import styles from './BraindateCard.style';

import BraindateCardCheckInStatus from './BraindateCardCheckInStatus';
import BraindateCardLastUpdate from './BraindateCardLastUpdate';
import BraindateCardStatus from './BraindateCardStatus';
import usePeriodicRender from 'src/shared/app/base/hook/usePeriodicRender';
import braindateMessages from 'src/shared/app/braindate/l10n/braindateL10n';
import BraindateCardGroupAvatar from 'src/shared/app/conversation/component/avatar/BraindateCardGroupAvatar';
import useFeatureDecisions from 'src/shared/app/feature/hook/useFeatureDecisions';
import UserAvatar, {
  medium,
} from 'src/shared/components/domain/user/avatar/UserAvatar';
import {
  isBraindateCheckInReady,
  isBraindateToday,
  isActiveBraindate,
} from 'src/shared/domain/braindate/util/braindateUtil';
import {
  getConversationOtherParticipants,
  getConversationOtherFullNames,
  getConversationParticipantsFirstNamesAsString,
} from 'src/shared/domain/conversation/util/conversationUtil';
import useEventAccentColor from 'src/shared/domain/event/hook/useEventAccentColor';
import useSelf from 'src/shared/domain/user/hook/useSelf';
import userMessages from 'src/shared/domain/user/l10n/userL10n';
import NotificationBadge from 'src/shared/ui/component/messaging/NotificationBadge';

import type { Braindate } from '@e180/domain/lib/braindate/type';

type Props = {
  braindate: Braindate,
  showOwnCheckInStatus?: boolean,
  isInactive?: boolean,
};

const useStyles = createUseStyles(styles);

const BraindateCard = ({
  braindate,
  showOwnCheckInStatus,
  isInactive,
}: Props): React.Node => {
  const id = getBraindateId(braindate);
  const conversation = getBraindateConversation(braindate);
  const topic = getConversationTopic(conversation);
  const unreadCount = getConversationUnreadNotificationsCount(conversation);
  const isDeclined = isBraindateDeclined(braindate);
  const isCancelled = isBraindateCancelled(braindate);
  const isGroupBraindate = isGroupTopic(topic);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  usePeriodicRender(60);

  const user = useSelf();
  const intl = useIntl();
  const { isSelfCheckInEnabled } = useFeatureDecisions();
  const [accentColor, darkAccentColor] = useEventAccentColor();

  const { locale } = intl;

  const name = isGroupBraindate
    ? getConversationParticipantsFirstNamesAsString(conversation, true)
    : user
    ? getConversationOtherFullNames(conversation, user)[0]
    : '';
  const otherParticipants = user
    ? getConversationOtherParticipants(conversation, user)
    : [];
  const [other] = otherParticipants;
  const topicTitle = getTopicTitle(topic, locale);

  let title = '';

  if (!isGroupBraindate && other) {
    const position = getUserPosition(other);
    const company = getUserCompany(other);

    if (position && company) {
      title = intl.formatMessage(userMessages.fullPosition, {
        position,
        company,
      });
    } else {
      title = position || company;
    }
  }

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({ accentColor, darkAccentColor });

  const rootClasses = classNames(classes.root, {
    [classes['root-isInactive']]: isInactive,
    [classes['root-isDeclined']]: isDeclined || isCancelled,
    [classes['root-isToday']]:
      isActiveBraindate(braindate) && isBraindateToday(braindate),
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const ns = 'braindate-card';
  const statusId = `${ns}-status-${id}`;
  const lastUpdateId = `${ns}-update-${id}`;

  const avatarProps = {
    variant: medium,
    alt: '',
  };

  const avatarElt = isGroupBraindate ? (
    <BraindateCardGroupAvatar {...avatarProps} conversation={conversation} />
  ) : (
    other && <UserAvatar {...avatarProps} user={other} showBadge />
  );
  const titleElt = title && <span className={classes.title}>{title}</span>;
  // $FlowIssue: `unreadCount` is a number in the condition
  const badgeElt = isNumber(unreadCount) && unreadCount > 0 && (
    <NotificationBadge layoutClass={classes.badge} />
  );
  const bottomElt =
    isSelfCheckInEnabled && isBraindateCheckInReady(braindate) ? (
      <BraindateCardCheckInStatus
        braindate={braindate}
        user={user}
        showOwnCheckInStatus={showOwnCheckInStatus}
      />
    ) : (
      <div className={classes.bottom}>
        <span id={statusId}>
          <BraindateCardStatus braindate={braindate} />
        </span>
        <span id={lastUpdateId}>
          <BraindateCardLastUpdate braindate={braindate} />
        </span>
      </div>
    );

  const tagName = isInactive ? 'div' : Link;
  const props = {
    className: rootClasses,
    to: isInactive ? null : getBraindatePath(braindate),
    'aria-label': intl.formatMessage(braindateMessages.braindateAriaLabel, {
      topic: topicTitle,
      name,
    }),
    'aria-describedby': `${statusId} ${lastUpdateId}`,
  };

  return React.createElement(
    tagName,
    // $FlowIssue: ?
    props,
    <React.Fragment>
      <div className={classes.body}>
        {avatarElt}
        <span className={classes.text}>
          <span className={classes.name}>{name}</span>
          {titleElt}
          <span className={classes.topic}>{topicTitle}</span>
        </span>
        {badgeElt}
      </div>
      {bottomElt}
    </React.Fragment>,
  );
};

export default compose(React.memo)(BraindateCard);
